'use client';

import Link from '@eliadmin/core/src/components/link';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MdOutlineLocalPhone, MdOutlineLocationOn } from 'react-icons/md';

export type TopbarProps = {
  locale?: string;
  generalLayout: GeneralLayout['data'];
  /** Kept for the shared Header contract — the Figma contact strip carries no menu links. */
  menu?: Menu;
};

/**
 * `header-bar — kontaktný pás` (Figma 475:49380): a near-black strip with the showroom address
 * and phone centred in it. 12px vertical padding, 24px between the two items, 18px icons,
 * Montserrat 14px — the phone in bold.
 *
 * Below `md` the two items don't fit side by side, so they keep the project's existing
 * cross-fade cycle in a single slot.
 */
export function Topbar({ generalLayout }: TopbarProps) {
  const contact = generalLayout?.contact;
  const address = [contact?.address, contact?.city].filter(Boolean).join(', ');
  const phone = contact?.phone;

  return (
    <div className={cn('custom-section', 'bg-[#251D17] py-3', 'ea-topbar')}>
      <div className={cn('custom-container', 'flex items-center justify-center gap-6')}>
        {/* Mobile: address and phone cycle through the same slot. */}
        <div className="relative flex w-full items-center justify-center md:hidden">
          <div className="topbar-address-cycle absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 whitespace-nowrap">
            <MdOutlineLocationOn size={18} className="shrink-0 text-white" aria-hidden />
            <span className="text-[13px] leading-[1.3] text-white">{address}</span>
          </div>
          <div className="topbar-phone-cycle flex items-center gap-2 whitespace-nowrap">
            <MdOutlineLocalPhone size={18} className="shrink-0 text-white" aria-hidden />
            <span className="text-[13px] font-bold leading-[1.3] text-white">{phone}</span>
          </div>
        </div>

        {/* Desktop: both side by side, centred. */}
        {!!address && (
          <div className="hidden items-center gap-2 md:flex">
            <MdOutlineLocationOn size={18} className="shrink-0 text-white" aria-hidden />
            <span className="text-right text-sm leading-[1.3] text-white">{address}</span>
          </div>
        )}
        {!!phone && (
          <Link
            prefetch={false}
            href={`tel:${phone.replace(/\s+/g, '')}`}
            className="generic-hover hidden items-center gap-2 md:flex"
          >
            <MdOutlineLocalPhone size={18} className="shrink-0 text-white" aria-hidden />
            <span className="text-right text-sm font-bold leading-[1.3] text-white">{phone}</span>
          </Link>
        )}
      </div>
    </div>
  );
}
